// Helpers + tab UI for the Guest Preferences list on the Customer detail
// page. A "preference" is a titled staff-only note that can be flagged as
// booking-time and/or payment-time; when flagged, it fires the corresponding
// popup in the New Appointment modal or on the Checkout page.
//
// Structure of each entry (matches the backend `guest_preferences` field on
// the customer document):
//   { id, title, description, is_booking_alert, is_payment_alert, created_at }

import React, { useEffect, useState } from "react";
import { Bell, CreditCard, Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `pref-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

// Seed the list from the legacy string fields once so operators don't lose
// their old alerts when this feature ships. Called from the load effect on
// BrandCustomerDetail. When the customer already has `guest_preferences`
// entries we return them as-is.
export function hydrateGuestPreferences(customer) {
  const list = Array.isArray(customer?.guest_preferences) ? customer.guest_preferences : [];
  if (list.length) return list;
  const seeded = [];
  const now = new Date().toISOString();
  const b = (customer?.booking_alert || "").trim();
  const p = (customer?.payment_alert || "").trim();
  if (b) {
    seeded.push({
      id: newId(),
      title: "Booking alert",
      description: b,
      is_booking_alert: true,
      is_payment_alert: false,
      created_at: now,
    });
  }
  if (p) {
    seeded.push({
      id: newId(),
      title: "Payment alert",
      description: p,
      is_booking_alert: false,
      is_payment_alert: true,
      created_at: now,
    });
  }
  return seeded;
}

// Read the popup entries a caller (calendar, checkout) should show. Falls
// back to the legacy string fields when the customer has no structured list
// yet. `kind` is either "booking" or "payment".
export function collectAlerts(customer, kind) {
  const list = Array.isArray(customer?.guest_preferences) ? customer.guest_preferences : [];
  if (list.length) {
    const flag = kind === "booking" ? "is_booking_alert" : "is_payment_alert";
    return list
      .filter((p) => p && p[flag])
      .map((p) => ({ title: (p.title || "").trim() || "Alert", description: (p.description || "").trim() }))
      .filter((p) => p.description);
  }
  const legacy = ((kind === "booking" ? customer?.booking_alert : customer?.payment_alert) || "").trim();
  return legacy ? [{ title: kind === "booking" ? "Booking alert" : "Payment alert", description: legacy }] : [];
}

// ─── Tab body ─────────────────────────────────────────────────────────────
export default function GuestPreferencesTab({ prefs, onChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null | preference-in-progress

  const openAdd = () => {
    setEditing({
      id: null,
      title: "",
      description: "",
      is_booking_alert: true,
      is_payment_alert: false,
    });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing({ ...p });
    setDialogOpen(true);
  };
  const commit = (draft) => {
    // Ignore submits that carry no useful content.
    if (!draft.title.trim() && !draft.description.trim()) {
      setDialogOpen(false);
      return;
    }
    // Update or append based on `id`. New rows get a UUID + timestamp so the
    // parent can send the list straight to the API without further work.
    const now = new Date().toISOString();
    const clean = {
      id: draft.id || newId(),
      title: draft.title.trim(),
      description: draft.description.trim(),
      is_booking_alert: !!draft.is_booking_alert,
      is_payment_alert: !!draft.is_payment_alert,
      created_at: draft.created_at || now,
      updated_at: now,
    };
    const next = draft.id
      ? (prefs || []).map((p) => (p.id === draft.id ? clean : p))
      : [...(prefs || []), clean];
    onChange(next);
    setDialogOpen(false);
  };
  const remove = (id) => onChange((prefs || []).filter((p) => p.id !== id));

  return (
    <Card className="rounded-lg" data-testid="guest-preferences-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-medium">Guest preferences</h2>
            <p className="text-[11px] text-muted-foreground max-w-md mt-0.5">
              Staff-only notes about this guest. Tick which alerts should pop up automatically. These are never shown to the customer.
            </p>
          </div>
          <Button size="sm" onClick={openAdd} className="rounded-full gap-1" data-testid="pref-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add new
          </Button>
        </div>

        {(!prefs || prefs.length === 0) && (
          <div className="text-center text-sm text-muted-foreground border border-dashed border-border rounded-md py-8" data-testid="pref-empty-state">
            No preferences yet. Click <span className="font-medium">Add new</span> to note something the team should know.
          </div>
        )}

        <div className="space-y-2">
          {(prefs || []).map((p) => (
            <div
              key={p.id}
              className="flex items-start gap-3 border border-border rounded-md p-3 hover:bg-secondary/30 transition-colors"
              data-testid={`pref-row-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-sm">{p.title || <em className="text-muted-foreground">Untitled</em>}</span>
                  {p.is_booking_alert && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] uppercase tracking-wider" data-testid={`pref-tag-booking-${p.id}`}>
                      <Bell className="w-3 h-3" /> Booking
                    </span>
                  )}
                  {p.is_payment_alert && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-800 text-[10px] uppercase tracking-wider" data-testid={`pref-tag-payment-${p.id}`}>
                      <CreditCard className="w-3 h-3" /> Payment
                    </span>
                  )}
                </div>
                {p.description && (
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">{p.description}</p>
                )}
              </div>
              <div className="flex-shrink-0 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => openEdit(p)}
                  className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                  aria-label="Edit"
                  data-testid={`pref-edit-${p.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  aria-label="Delete"
                  data-testid={`pref-delete-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <PreferenceDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onCommit={commit} />
      </CardContent>
    </Card>
  );
}

// Nested dialog — kept in this file to keep the tab body self-contained.
function PreferenceDialog({ open, onOpenChange, initial, onCommit }) {
  const [draft, setDraft] = useState(initial || null);
  useEffect(() => { setDraft(initial || null); }, [initial]);

  if (!draft) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pref-dialog">
        <DialogHeader>
          <DialogTitle>{draft.id ? "Edit preference" : "New preference"}</DialogTitle>
          <DialogDescription>Staff-only note. Never shown to the customer.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="e.g. Prefers Ayu · pregnancy-safe products"
              className="mt-1"
              data-testid="pref-title-input"
              autoFocus
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Add any detail the team should know…"
              rows={3}
              className="mt-1"
              data-testid="pref-description-input"
            />
          </div>
          <div className="pt-1">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Pop up when</Label>
            <div className="flex flex-wrap gap-4 mt-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="pref-toggle-booking-label">
                <Checkbox
                  checked={!!draft.is_booking_alert}
                  onCheckedChange={(v) => setDraft({ ...draft, is_booking_alert: !!v })}
                  data-testid="pref-toggle-booking"
                />
                <Bell className="w-3.5 h-3.5 text-amber-700" />
                Booking alert
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="pref-toggle-payment-label">
                <Checkbox
                  checked={!!draft.is_payment_alert}
                  onCheckedChange={(v) => setDraft({ ...draft, is_payment_alert: !!v })}
                  data-testid="pref-toggle-payment"
                />
                <CreditCard className="w-3.5 h-3.5 text-rose-700" />
                Payment alert
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Booking → pops up in the New Appointment modal when picking this guest.<br />
              Payment → pops up when the guest&apos;s cart is opened on the Checkout page.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="pref-cancel-btn">Cancel</Button>
          <Button onClick={() => onCommit(draft)} data-testid="pref-save-btn">Save preference</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
